import { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Unlock, RefreshCcw, Loader2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

export function LockedAccountsTab({ token }) {
  const [lockouts, setLockouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState('');

  const fetchLockouts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/security/lockouts`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) {
        const d = await r.json();
        setLockouts(d.lockouts || []);
      }
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchLockouts(); }, [fetchLockouts]);

  const unlock = async (identifier) => {
    setUnlocking(identifier);
    try {
      const r = await fetch(`${API_URL}/api/admin/security/lockouts/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ identifier })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to unlock');
      toast.success(d.message || 'Account unlocked');
      setLockouts(prev => prev.filter(l => l.identifier !== identifier));
    } catch (e) { toast.error(e.message); }
    finally { setUnlocking(''); }
  };

  return (
    <Card data-testid="locked-accounts-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="h-5 w-5 text-amber-500" /> Locked Accounts
            </CardTitle>
            <CardDescription>
              Accounts temporarily locked after too many failed login, 2FA or OTP attempts. Unlock staff who simply forgot their password.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchLockouts} disabled={loading} data-testid="refresh-lockouts-btn">
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : lockouts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="no-lockouts">
            <CheckCircle2 className="h-10 w-10 text-green-500 mb-3" />
            <p className="font-medium text-secondary-900 dark:text-white">No locked accounts</p>
            <p className="text-sm text-muted-foreground">Everything looks secure right now.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {lockouts.map(l => (
              <div key={l.identifier} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border bg-amber-50/50 dark:bg-amber-900/10" data-testid={`lockout-row`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-secondary-900 dark:text-white truncate">{l.target}</span>
                    <Badge variant="secondary" className="text-[10px]">{l.type}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Unlocks in ~{l.minutes_remaining} min{l.ip ? ` · IP ${l.ip}` : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => unlock(l.identifier)} disabled={unlocking === l.identifier}
                  className="shrink-0" data-testid={`unlock-btn`}>
                  {unlocking === l.identifier ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Unlock className="h-4 w-4 mr-1" /> Unlock</>}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LockedAccountsTab;
